import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { TrendingUp } from 'lucide-react'
import {
  Area,
  AreaChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { trpc } from '@/config/trpc'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { InfoTooltip } from './info-tooltip'

export function PerformanceDecayCard({ platforms }: { platforms: PlatformType[] }) {
  const { data, isLoading } = trpc.analytics.performanceDecay.useQuery({
    platforms: platforms.length > 0 ? platforms : undefined,
  })

  // 80% threshold marker — answers "by what age have most posts captured the
  // bulk of their engagement?" Common social-media intuition is that posts
  // peak fast; this card shows whether that's true for *your* posts.
  const chartData = data?.byBucket?.map((b) => ({
    label: b.label,
    pct: Math.round(b.avgPctOfFinal * 100),
    postCount: b.postCount,
  }))

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <TrendingUp size={14} className="text-muted-foreground" />
          Engagement Curve
          <InfoTooltip>
            How quickly your posts collect their final engagement. Most platforms front-load
            engagement in the first hours; if your curve climbs slowly, you may benefit from
            longer-tail content or follow-up amplification.
          </InfoTooltip>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="bg-muted h-32 animate-pulse rounded" />}

        {data && !data.hasEnoughData && (
          <p className="text-muted-foreground py-4 text-xs">
            Need <span className="font-medium">{data.minPosts}</span> posts with sync history to map
            a curve — you have <span className="font-medium">{data.totalPostsWithHistory}</span>.
          </p>
        )}

        {data && data.hasEnoughData && chartData && chartData.length > 0 && (
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 6, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="decayFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(262, 83%, 58%)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(262, 83%, 58%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: 'currentColor' }}
                  tickLine={false}
                  axisLine={false}
                  className="text-muted-foreground"
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 10, fill: 'currentColor' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `${v}%`}
                  className="text-muted-foreground"
                  width={36}
                />
                <ReferenceLine
                  y={80}
                  stroke="currentColor"
                  className="text-muted-foreground/30"
                  strokeDasharray="3 3"
                />
                <Tooltip
                  cursor={{ stroke: 'hsl(262, 83%, 58%)', strokeOpacity: 0.2 }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.[0]) return null
                    const p = payload[0].payload as {
                      label: string
                      pct: number
                      postCount: number
                    }
                    return (
                      <div className="bg-popover text-popover-foreground rounded-md border px-2.5 py-1.5 text-xs shadow-sm">
                        <div className="font-medium">{p.label}</div>
                        <div className="text-muted-foreground">{p.pct}% of final</div>
                        <div className="text-muted-foreground/70 text-[10px]">
                          {p.postCount} posts
                        </div>
                      </div>
                    )
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="pct"
                  stroke="hsl(262, 83%, 58%)"
                  strokeWidth={2}
                  fill="url(#decayFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
